import { useState, ReactNode } from "react";
import type { LucideIcon } from "lucide-react";
import { Eye, EyeOff } from "lucide-react";
import { Label } from "@/components/ui/label";

interface AuthInputProps {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  icon: LucideIcon;
  type?: string;
  placeholder?: string;
  required?: boolean;
  autoComplete?: string;
  /** Focuses the field when it mounts. */
  autoFocus?: boolean;
  /** Renders a password field with a show/hide toggle. */
  secret?: boolean;
  /** Element rendered to the right of the label (e.g. a "Forgot password?" link). */
  action?: ReactNode;
}

export function AuthInput({
  id,
  label,
  value,
  onChange,
  icon: Icon,
  type = "text",
  placeholder,
  required,
  autoComplete,
  autoFocus,
  secret,
  action,
}: AuthInputProps) {
  const [show, setShow] = useState(false);
  const resolvedType = secret ? (show ? "text" : "password") : type;

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <Label
          htmlFor={id}
          className="text-[13px] font-medium"
          style={{ color: "var(--landing-text)" }}
        >
          {label}
          {required && <span className="ml-1 text-[var(--a2m-sig-ink-hi)]">*</span>}
        </Label>
        {action}
      </div>
      <div className="auth-input flex h-12 items-center gap-3 rounded-xl px-3.5">
        <Icon
          className="h-4 w-4 shrink-0"
          style={{ color: "rgba(var(--acc2-rgb),0.7)" }}
          aria-hidden="true"
        />
        <input
          id={id}
          type={resolvedType}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          required={required}
          autoComplete={autoComplete}
          autoFocus={autoFocus}
          className="min-w-0 flex-1 border-0 bg-transparent text-sm outline-none"
          style={{ color: "var(--landing-text)" }}
        />
        {secret && (
          <button
            type="button"
            onClick={() => setShow((s) => !s)}
            className="shrink-0 transition-colors hover:text-[var(--acc2)]"
            style={{ color: "var(--landing-text-muted)" }}
            aria-label={show ? "Hide password" : "Show password"}
          >
            {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        )}
      </div>
    </div>
  );
}
